import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useRaffles } from '../../api/queries';
import { qs } from '../../api/client';
import { Ic } from '../../components/Ic';
import { Empty } from '../../components/Empty';
import { RAFFLE_FILTERS, statusMeta } from '../../lib/raffleStatus';
import { badgeColor, siteMeta } from '../../lib/siteMeta';
import { relTime, dateTime } from '../../lib/time';
import { RaffleDetailModal } from './RaffleDetailModal';
import { usePresence } from '../../components/usePresence';

const PAGE_SIZE = 50;

const SORTS = [
  { value: 'detected', label: 'Newest first', order: 'desc' },
  { value: 'closes', label: 'Closing soon', order: 'asc' },
  { value: 'product', label: 'Title A–Z', order: 'asc' },
];

export function RafflesView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();

  // Filters live in the URL so a filtered view is shareable — an improvement
  // over the prototype, which kept them in component state.
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? 'all';
  const site = params.get('site') ?? 'all';
  const sort = params.get('sort') ?? 'detected';
  const page = Math.max(0, Number(params.get('page') ?? 0) || 0);
  const order = SORTS.find((s) => s.value === sort)?.order ?? 'desc';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page'); // any filter change resets paging
    setParams(next, { replace: true });
  };

  const query = {
    q: q || undefined,
    status: status === 'all' ? undefined : status,
    site: site === 'all' ? undefined : site,
    sort,
    order,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const { data, isLoading, error } = useRaffles(query);
  const selected = id ? data?.items.find((r) => String(r.id) === id) : undefined;
  const detail = usePresence(selected ? { raffle: selected } : undefined);
  const lastPage = data ? Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1) : 0;

  return (
    <div className="view-pad">
      <div className="sec-head">
        <h2>Raffles</h2>
        <span className="desc">
          {data ? `${data.total} matching` : error ? 'unavailable' : 'loading…'}
        </span>
        <span className="spacer" />
        {/* Exports the full filtered set, not just the visible page. */}
        <a
          className="btn ghost sm"
          href={`/api/raffles${qs({ ...query, limit: undefined, offset: undefined, format: 'csv' })}`}
        >
          <Ic.download className="ico" /> Export CSV
        </a>
      </div>

      <div className="pcard" style={{ marginBottom: 16 }}>
        <div className="body pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ position: 'relative' }}>
            <Ic.search
              style={{
                position: 'absolute',
                left: 11,
                top: 8.5,
                width: 15,
                height: 15,
                color: 'var(--fg-3)',
              }}
            />
            <input
              className="input"
              style={{ paddingLeft: 34 }}
              placeholder="Search product or site…"
              value={q}
              onChange={(e) => setParam('q', e.target.value)}
            />
          </div>

          <div className="chip-row">
            {RAFFLE_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`chip ${status === f.value ? 'on' : ''}`}
                onClick={() => setParam('status', f.value)}
              >
                {f.label}
                {data?.counts?.[f.value as keyof typeof data.counts] !== undefined && (
                  <span className="muted"> {data.counts[f.value as keyof typeof data.counts]}</span>
                )}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select
              className="input"
              style={{ width: 'auto', minWidth: 180 }}
              value={site}
              onChange={(e) => setParam('site', e.target.value)}
            >
              <option value="all">All sources</option>
              {data?.sites.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 'auto', minWidth: 160 }}
              value={sort}
              onChange={(e) => setParam('sort', e.target.value)}
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  Sort · {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isLoading && <div className="muted">Loading raffles…</div>}

      {error && (
        <Empty
          icon={Ic.alert}
          title="Could not load raffles"
          detail={error instanceof Error ? error.message : String(error)}
        />
      )}

      {!isLoading && !error && data?.items.length === 0 && (
        <Empty
          icon={Ic.search}
          title="No raffles match"
          detail="Try clearing the search or status filter."
        />
      )}

      {data && data.items.length > 0 && (
        <div className="pcard">
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Raffle</th>
                  <th>Source</th>
                  <th>Scraper</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Detected</th>
                  <th>Closes</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => {
                  const meta = statusMeta(r.status);
                  const stillOpen = r.cutoff !== null && r.cutoff * 1000 > Date.now();
                  return (
                    <tr
                      key={r.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/raffles/${r.id}`)}
                    >
                      <td className="cell-title">{r.product ?? '—'}</td>
                      <td>
                        <span className="retailer">
                          <span
                            className="swatch"
                            style={{ background: badgeColor(r.site) }}
                            title={siteMeta(r.site).short}
                          />
                          {r.site ?? '—'}
                        </span>
                      </td>
                      <td className="mono-sm">{r.scraper ?? '—'}</td>
                      <td className="mono-sm">{r.price ? `$${r.price}` : '—'}</td>
                      <td>
                        <span className={`pill ${meta.pill}`}>{meta.label}</span>
                      </td>
                      <td className="mono-sm" title={dateTime(r.detected_at)}>
                        {relTime(r.detected_at)}
                      </td>
                      <td className="mono-sm">
                        {r.cutoff ? (stillOpen ? relTime(r.cutoff) : 'closed') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {lastPage > 0 && (
            <div
              className="body pad"
              style={{ display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid var(--line-1)' }}
            >
              <button
                className="btn ghost sm"
                disabled={page === 0}
                onClick={() => setParam('page', String(page - 1))}
              >
                <Ic.chevLeft className="ico" /> Previous
              </button>
              <span className="muted" style={{ fontSize: 12 }}>
                Page {page + 1} of {lastPage + 1}
              </span>
              <span style={{ flex: 1 }} />
              <button
                className="btn ghost sm"
                disabled={page >= lastPage}
                onClick={() => setParam('page', String(page + 1))}
              >
                Next <Ic.chevRight className="ico" />
              </button>
            </div>
          )}
        </div>
      )}

      {detail.item && (
        <RaffleDetailModal {...detail.item} open={detail.open} onExited={detail.onExited} />
      )}
    </div>
  );
}
